/**
 * useAIHelperOnboarding — drives the "pulse + coachmark" onboarding hint
 * that teaches users they can tap words for AI help after a game ends.
 *
 * Shown after every completed game while the trigger is enabled.
 *
 * Timing strategy:
 *   1. When `enabled` flips to true, poll every 500ms for [data-ai-component]
 *      elements inside the container.
 *   2. Once found, wait 3 500ms (covers the 2 800ms celebration on wins and
 *      gives a comfortable pause after loss-reveal animations).
 *   3. Add the `ai-helper-attention` CSS class to ALL found elements (purple
 *      pulse glow) and expose the first in-viewport element as coachmarkTarget.
 *      An optional `coachmarkSelector` CSS selector can restrict which elements
 *      are eligible coachmark targets (e.g. only "word" elements, not titles).
 *   4. Auto-dismiss after 10s, or immediately when the AI panel opens.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useAIHelper } from '../contexts/AIHelperContext';
import { useAlertQueueState } from '../contexts/AlertQueueContext';
import { useGlobalModal } from '../contexts/ModalContext';
import { useResourceCelebration } from '../contexts/ResourceCelebrationContext';
import { useGameStartAccountOffer } from '../contexts/GameStartAccountOfferContext';

const ATTENTION_CLASS = 'ai-helper-attention';
const STORAGE_PREFIX = 'ai-helper-onboarding-';
const POST_DETECT_DELAY = 2000; // ms after elements found before showing
const AUTO_DISMISS_DELAY = 10000; // ms after showing before auto-dismiss
const POLL_INTERVAL = 500; // ms between element polls

/* ── localStorage helpers (count-only, no gating) ───────────────────── */

function getShownCount(gameSlug) {
  try {
    return parseInt(localStorage.getItem(STORAGE_PREFIX + gameSlug) || '0', 10) || 0;
  } catch {
    return 0;
  }
}

function incrementShownCount(gameSlug) {
  try {
    const next = getShownCount(gameSlug) + 1;
    localStorage.setItem(STORAGE_PREFIX + gameSlug, String(next));
  } catch {
    // localStorage unavailable — silently ignore
  }
}

/* ── Viewport helper ────────────────────────────────────────────────── */

function isInViewport(el) {
  const rect = el.getBoundingClientRect();
  return (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
    rect.right <= (window.innerWidth || document.documentElement.clientWidth)
  );
}

/* ── Hook ───────────────────────────────────────────────────────────── */

export function useAIHelperOnboarding(containerRef, enabled, gameSlug, coachmarkSelector, requestVersion = 0) {
  const [coachmarkTarget, setCoachmarkTarget] = useState(null);
  const activeRef = useRef(false); // true while pulse is showing
  const elementsRef = useRef([]); // elements we added the class to
  const timersRef = useRef([]); // pending setTimeout ids
  const hasShownThisCycleRef = useRef(false); // prevent re-show loops while enabled
  const { isOpen } = useAIHelper();
  const { currentAlert, hasPendingAlerts } = useAlertQueueState();
  const { isOpen: modalOpen } = useGlobalModal();
  const { flights } = useResourceCelebration();
  const { hasPendingOffer = false } = useGameStartAccountOffer();
  const shownVersionRef = useRef(null);

  // Stable dismiss callback
  const dismissOnboarding = useCallback(() => {
    // Remove attention class from all marked elements
    for (const el of elementsRef.current) {
      el.classList.remove(ATTENTION_CLASS);
    }
    elementsRef.current = [];
    setCoachmarkTarget(null);
    activeRef.current = false;

    // Clear any remaining timers
    for (const id of timersRef.current) clearTimeout(id);
    timersRef.current = [];
  }, []);

  // Dismiss when the AI panel opens (user clicked a word)
  useEffect(() => {
    if (isOpen && activeRef.current) {
      dismissOnboarding();
    }
  }, [isOpen, dismissOnboarding]);

  // Dismiss if a reward/streak alert appears while onboarding is visible.
  useEffect(() => {
    if (currentAlert && activeRef.current) {
      dismissOnboarding();
    }
  }, [currentAlert, dismissOnboarding]);

  // Main effect: start the poll → delay → show sequence
  useEffect(() => {
    // Gate: nothing to do if not enabled
    if (!enabled) {
      hasShownThisCycleRef.current = false;
      return;
    }
    // Wait until the alert queue is completely idle.
    if (hasPendingAlerts || flights.length > 0 || hasPendingOffer || modalOpen || isOpen) return;
    if (shownVersionRef.current !== requestVersion) hasShownThisCycleRef.current = false;
    // Show at most once per enabled cycle.
    if (hasShownThisCycleRef.current) return;

    const container = containerRef.current;
    if (!container) return;

    let cancelled = false;

    const addTimer = (fn, ms) => {
      const id = setTimeout(() => {
        if (cancelled) return;
        fn();
      }, ms);
      timersRef.current.push(id);
      return id;
    };

    // Phase 1: poll for data-ai-component elements
    const pollId = setInterval(() => {
      if (cancelled) { clearInterval(pollId); return; }

      const els = container.querySelectorAll('[data-ai-component]');
      if (els.length === 0) return;

      // Elements found — stop polling, start delay
      clearInterval(pollId);

      // Phase 2: wait for celebrations / settle
      addTimer(() => {
        if (cancelled) return;

        // Re-query in case the DOM changed during the delay
        const freshEls = container.querySelectorAll('[data-ai-component]');
        if (freshEls.length === 0) return;

        // Phase 3: activate pulse + coachmark
        activeRef.current = true;
        const collected = [];

        for (const el of freshEls) {
          el.classList.add(ATTENTION_CLASS);
          collected.push(el);
        }

        hasShownThisCycleRef.current = true;
        shownVersionRef.current = requestVersion;

        // Keep tracking exposures for possible future gating, but do not gate now.
        if (gameSlug) {
          incrementShownCount(gameSlug);
        }

        elementsRef.current = collected;

        // If a coachmarkSelector was provided, only those elements are
        // eligible targets (e.g. words, not titles).  The pulse still
        // applies to ALL data-ai-component elements.
        const candidates = coachmarkSelector
          ? Array.from(container.querySelectorAll(coachmarkSelector))
          : collected;
        const firstVisible = candidates.find(isInViewport);
        const target = firstVisible || candidates[0] || collected[0] || null;
        if (!firstVisible) target?.scrollIntoView?.({ block: 'center', behavior: 'auto' });
        setCoachmarkTarget(target);

        // Phase 4: auto-dismiss after timeout
        addTimer(() => {
          if (cancelled) return;
          dismissOnboarding();
        }, AUTO_DISMISS_DELAY);
      }, POST_DETECT_DELAY);
    }, POLL_INTERVAL);

    timersRef.current.push(pollId);

    return () => {
      cancelled = true;
      clearInterval(pollId);
      // Clean up classes and timers
      for (const el of elementsRef.current) {
        el.classList.remove(ATTENTION_CLASS);
      }
      elementsRef.current = [];
      for (const id of timersRef.current) clearTimeout(id);
      timersRef.current = [];
      activeRef.current = false;
      setCoachmarkTarget(null);
    };
  }, [
    enabled,
    gameSlug,
    containerRef,
    coachmarkSelector,
    dismissOnboarding,
    hasPendingAlerts,
    flights.length,
    hasPendingOffer,
    modalOpen,
    isOpen,
    requestVersion,
  ]);

  return { coachmarkTarget, dismissOnboarding };
}
